import { useState } from 'react';
import { Plus, Search, Truck } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useCreateSupplierMutation, useSuppliersQuery, useUpdateSupplierMutation, type Supplier,
} from '@/api/endpoints';
import { apiErrorMessage, apiFieldErrors } from '@/api/baseApi';
import { formatMoney } from '@/lib/format';
import {
  Alert, Badge, Button, Card, EmptyState, ErrorState, Input, LoadingState, Modal,
  PageHeader, Pagination, Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { SummaryCards } from '@/components/SummaryCards';
import { SupplierDetailModal } from './SupplierDetailModal';

const emptyForm = {
  name: '',
  code: '',
  contactPerson: '',
  phone: '',
  email: '',
  city: '',
  paymentTermDays: '',
  openingBalance: '',
};

export function SuppliersPage() {
  const format = useFormat();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'owing'>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [form, setForm] = useState(emptyForm);

  const { data, isLoading, isError, error, refetch, isFetching } = useSuppliersQuery({
    page,
    limit: 25,
    search: search || undefined,
    owingOnly: filter === 'owing' || undefined,
  });
  const [createSupplier, createState] = useCreateSupplierMutation();
  const [updateSupplier, updateState] = useUpdateSupplierMutation();

  const saving = createState.isLoading || updateState.isLoading;
  const fieldErrors = apiFieldErrors(editing ? updateState.error : createState.error);

  const startEdit = (supplier: Supplier): void => {
    setEditing(supplier);
    setForm({
      name: supplier.name,
      code: supplier.code ?? '',
      contactPerson: supplier.contactPerson ?? '',
      phone: supplier.phone ?? '',
      email: supplier.email ?? '',
      city: supplier.city ?? '',
      paymentTermDays: '',
      // An opening balance is what they were owed when they were added; the
      // ledger has carried it ever since, so it is not editable here.
      openingBalance: '',
    });
  };

  const submit = async (): Promise<void> => {
    const body = {
      name: form.name,
      code: form.code || undefined,
      contactPerson: form.contactPerson || undefined,
      phone: form.phone || undefined,
      email: form.email || undefined,
      city: form.city || undefined,
      paymentTermDays: form.paymentTermDays ? Number(form.paymentTermDays) : undefined,
    };

    const saved = editing
      ? await updateSupplier({ id: editing.id, body }).unwrap().catch(() => null)
      : await createSupplier({
          ...body,
          openingBalance: form.openingBalance ? Number(form.openingBalance) : undefined,
        })
          .unwrap()
          .catch(() => null);

    if (saved) {
      setCreating(false);
      setEditing(null);
      setForm(emptyForm);
      if (!editing) setOpenId(saved.id);
    }
  };

  return (
    <>
      <PageHeader
        title="Suppliers"
        description="Who you buy from, and what you owe them."
        action={
          <Can permission={P.SUPPLIER_CREATE}>
            <Button icon={Plus} onClick={() => setCreating(true)}>New supplier</Button>
          </Can>
        }
      />

      {data?.summary && (
        <SummaryCards
          stats={[
            { label: 'Suppliers', value: String(data.summary.count) },
            {
              label: 'We owe them',
              value: formatMoney(String(data.summary.owed), format),
              hint: 'across these suppliers',
              tone: 'owed',
            },
          ]}
        />
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
          <div className="min-w-48 flex-1">
            <Input
              label="Search"
              icon={Search}
              placeholder="Name, code, phone or email"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-full sm:w-52">
            <Select
              label="Show"
              value={filter}
              onChange={(event) => {
                setFilter(event.target.value as typeof filter);
                setPage(1);
              }}
            >
              <option value="all">All suppliers</option>
              <option value="owing">We owe money</option>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={Truck}
            title={search || filter !== 'all' ? 'Nothing matches' : 'No suppliers yet'}
            description="Add a supplier to raise purchase orders against them."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Supplier</Th>
                  <Th>Contact</Th>
                  <Th>Phone</Th>
                  <Th numeric>We owe</Th>
                  <Th>Terms</Th>
                  <Th>Status</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className={isFetching ? 'opacity-60' : undefined}>
                {data?.items.map((supplier: Supplier) => (
                  <tr key={supplier.id} className="hover:bg-sunken">
                    <Td>
                      <button
                        type="button"
                        className="text-left font-medium hover:underline"
                        onClick={() => setOpenId(supplier.id)}
                      >
                        {supplier.name}
                      </button>
                      <p className="text-xs text-muted">{supplier.code}</p>
                    </Td>
                    <Td className="text-secondary">{supplier.contactPerson ?? '—'}</Td>
                    <Td className="text-secondary">{supplier.phone ?? '—'}</Td>
                    <Td
                      numeric
                      className={Number(supplier.balance) > 0 ? 'font-medium text-amber-600' : 'text-secondary'}
                    >
                      {formatMoney(supplier.balance, format)}
                    </Td>
                    <Td className="text-secondary">
                      {supplier.paymentTermDays > 0 ? `${supplier.paymentTermDays} days` : 'On demand'}
                    </Td>
                    <Td>
                      {supplier.isActive ? (
                        <Badge tone="success">Active</Badge>
                      ) : (
                        <Badge tone="neutral">Inactive</Badge>
                      )}
                    </Td>
                    <Td className="text-right">
                      <Can permission={P.SUPPLIER_UPDATE}>
                        <Button size="sm" variant="ghost" onClick={() => startEdit(supplier)}>
                          Edit
                        </Button>
                      </Can>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Modal
        open={creating || editing !== null}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        title={editing ? `Edit ${editing.name}` : 'New supplier'}
        description={
          editing
            ? 'Their details. What they are owed is carried by the ledger, not edited here.'
            : 'Leave the code blank and one is allocated from the numbering sequence.'
        }
        size="lg"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setCreating(false);
                setEditing(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={() => void submit()} loading={saving} disabled={!form.name}>
              {editing ? 'Save changes' : 'Create supplier'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {(editing ? updateState.error : createState.error) && (
            <Alert tone="danger">
              {apiErrorMessage(editing ? updateState.error : createState.error)}
            </Alert>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Name"
              required
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              error={fieldErrors.name}
            />
            <Input
              label="Code"
              placeholder="Allocated automatically"
              value={form.code}
              onChange={(event) => setForm({ ...form, code: event.target.value })}
              error={fieldErrors.code}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Input
              label="Contact person"
              value={form.contactPerson}
              onChange={(event) => setForm({ ...form, contactPerson: event.target.value })}
            />
            <Input
              label="Phone"
              value={form.phone}
              onChange={(event) => setForm({ ...form, phone: event.target.value })}
            />
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
              error={fieldErrors.email}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Input
              label="City"
              value={form.city}
              onChange={(event) => setForm({ ...form, city: event.target.value })}
            />
            <Input
              label="Payment terms (days)"
              type="number"
              min="0"
              value={form.paymentTermDays}
              onChange={(event) => setForm({ ...form, paymentTermDays: event.target.value })}
              hint="How long we have to pay."
            />
            <Input
              label="Opening balance"
              type="number"
              step="0.01"
              value={form.openingBalance}
              onChange={(event) => setForm({ ...form, openingBalance: event.target.value })}
              hint="What we already owed them."
            />
          </div>
        </div>
      </Modal>

      {openId && <SupplierDetailModal supplierId={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}
